import { Badge } from '~/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useTranslationLocales, type TranslationLocale } from '~/hooks/useTranslationLocales';
import { cn } from '~/utils/cn';

import { useProductTranslations } from '../../hooks/use-translations';
import type {
  LocalizationQaStatus,
  LocalizedContent,
  LocalizedFields,
} from '../../types/translations.types';
import { diffWords, type DiffSegment } from '../../utils/word-diff';

const QA_STYLE: Record<LocalizationQaStatus, string> = {
  PASS: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  FLAG: 'border-amber-200 bg-amber-50 text-amber-700',
  FAIL: 'border-red-200 bg-red-50 text-red-700',
};

const QA_LABEL: Record<LocalizationQaStatus, string> = {
  PASS: 'QA pass',
  FLAG: 'QA flag',
  FAIL: 'Held back',
};

/** One piece of a field's text: a list item (highlight / detail value) or the whole value. */
interface Part {
  /** Muted, never diffed — product-detail attribute names are GMC keys and stay English. */
  prefix?: string;
  text: string;
}

/** The rows of the comparison — the fields a translation carries, in feed order. */
const ROWS: { label: string; list?: 'bullet' | 'plain'; parts: (f: LocalizedFields) => Part[] }[] =
  [
    { label: 'Title', parts: (f) => (f.title ? [{ text: f.title }] : []) },
    { label: 'Short title', parts: (f) => (f.short_title ? [{ text: f.short_title }] : []) },
    { label: 'Description', parts: (f) => (f.description ? [{ text: f.description }] : []) },
    {
      label: 'Highlights',
      list: 'bullet',
      parts: (f) => (f.product_highlight ?? []).map((text) => ({ text })),
    },
    {
      label: 'Product details',
      list: 'plain',
      parts: (f) =>
        (f.product_detail ?? []).map((d) => ({
          prefix: `${d.attribute_name}:`,
          text: d.attribute_value,
        })),
    },
  ];

/**
 * English variants (en-GB) are the same text re-spelled, so a word diff against the en-US
 * source is meaningful; a real translation (es-ES) differs everywhere, so it gets no word
 * highlights — only a "Same as English" flag for a field that came back untranslated.
 */
function isEnglishVariant(locale: string, locales: readonly TranslationLocale[] | undefined) {
  const known = locales?.find((l) => l.code.toLowerCase() === locale.toLowerCase());
  if (known) return !known.isTranslation;
  return locale.toLowerCase().startsWith('en-');
}

/** Per-cell verdict shown as a chip above the value. */
interface CellSummary {
  changedWords: number;
  /** Non-empty and word-for-word the English source. */
  identical: boolean;
}

/** Diff one field's parts against the source's, part by part (lists align by position). */
function diffParts(source: Part[], target: Part[]) {
  const diffs = target.map((part, index) => diffWords(source[index]?.text ?? '', part.text));
  // A source item the target dropped entirely counts as removed words too.
  const dropped = source.slice(target.length).map((part) => diffWords(part.text, ''));
  const changedWords = [...diffs, ...dropped].reduce((sum, d) => sum + d.changedWords, 0);
  return { diffs, changedWords };
}

/** Grammarly-style inline marks: changed/added words underlined, dropped words struck through. */
function DiffText({ segments, sourceLabel }: { segments: DiffSegment[]; sourceLabel: string }) {
  return (
    <>
      {segments.map((seg, index) => {
        const key = `${String(index)}-${seg.kind}`;
        if (seg.kind === 'same') return <span key={key}>{seg.text}</span>;
        const tip =
          seg.kind === 'changed'
            ? `Was “${seg.was.trim()}” in ${sourceLabel}`
            : seg.kind === 'added'
              ? `Added — not in ${sourceLabel}`
              : `Removed from ${sourceLabel}`;
        return (
          <Tooltip key={key} delayDuration={150}>
            <TooltipTrigger asChild>
              <mark
                className={cn(
                  'cursor-help rounded-sm px-0.5 decoration-2 underline-offset-[3px]',
                  seg.kind === 'removed'
                    ? 'bg-transparent text-red-600 line-through decoration-red-400'
                    : 'text-foreground bg-emerald-50 underline decoration-emerald-500',
                )}
              >
                {seg.text}
                {/* The hover tip, for screen readers (a mark is not a focus stop). */}
                <span className="sr-only"> ({tip})</span>
              </mark>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              {tip}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </>
  );
}

/** A field's value: plain, or with diff marks when `diffs` is given (one per part). */
function FieldValue({
  parts,
  list,
  diffs,
  sourceLabel,
}: {
  parts: Part[];
  list: 'bullet' | 'plain' | undefined;
  diffs?: ReturnType<typeof diffParts>['diffs'];
  sourceLabel: string;
}) {
  if (parts.length === 0) return <>—</>;
  const body = (part: Part, index: number) => {
    const diff = diffs?.[index];
    return (
      <>
        {part.prefix && <span className="text-muted-foreground">{part.prefix} </span>}
        {diff ? <DiffText segments={diff.segments} sourceLabel={sourceLabel} /> : part.text}
      </>
    );
  };
  if (!list) return body(parts[0] ?? { text: '' }, 0);
  return (
    <ul className={cn('space-y-0.5', list === 'bullet' && 'list-disc pl-4')}>
      {parts.map((part, index) => (
        <li key={`${String(index)}-${part.prefix ?? ''}${part.text}`}>{body(part, index)}</li>
      ))}
    </ul>
  );
}

/** The chip above a translated cell. Variants: words changed / unchanged; translations: only
 *  the untranslated case, which is worth a second look. */
function CellChip({
  summary,
  variant,
  testId,
}: {
  summary: CellSummary;
  variant: boolean;
  testId: string;
}) {
  if (variant) {
    return summary.changedWords > 0 ? (
      <span
        className="mb-1 inline-block rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700"
        data-testid={`${testId}-changed-badge`}
      >
        {summary.changedWords} {summary.changedWords === 1 ? 'word' : 'words'} changed
      </span>
    ) : (
      <span
        className="text-muted-foreground mb-1 inline-block text-[10px]"
        data-testid={`${testId}-unchanged-badge`}
      >
        Unchanged
      </span>
    );
  }
  return summary.identical ? (
    <span
      className="mb-1 inline-block rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
      data-testid={`${testId}-same-as-english-badge`}
    >
      Same as English
    </span>
  ) : null;
}

interface LanguageCompareViewProps {
  /** Any id of the product's run; workflow-service resolves the scope. */
  workflowId: string;
  productId: string;
}

/**
 * The product's content side by side: English | each translated locale, one row per field.
 *
 * The English column is what the translations were MADE FROM (their stored `sourceFields`) —
 * the only English the right-hand columns can be judged against. When the product's English has
 * changed since, that locale's header says so (stale). Section and attribute NAMES stay English
 * in every column: they are GMC matching keys; only values translate.
 */
export function LanguageCompareView({ workflowId, productId }: LanguageCompareViewProps) {
  const { data, isPending, isError } = useProductTranslations(workflowId, productId);
  const locales = useTranslationLocales((data?.length ?? 0) > 0);

  if (isPending)
    return (
      <p className="text-muted-foreground p-5 text-sm" data-testid="translations-compare-loading">
        Loading translations…
      </p>
    );
  if (isError)
    return (
      <p className="text-destructive p-5 text-sm" data-testid="translations-compare-error">
        Could not load this product's translations.
      </p>
    );
  if (data.length === 0)
    return (
      <p className="text-muted-foreground p-5 text-sm" data-testid="translations-compare-empty">
        This product has not been translated. English (en-US) is its only language.
      </p>
    );

  // One column per locale (FULL preferred over SAMPLE when both exist — FULL is what ships).
  const columns = pickColumns(data);
  const english = columns[0]?.sourceFields ?? {};
  const nameOf = (code: string) =>
    locales.data?.find((l) => l.code.toLowerCase() === code.toLowerCase())?.displayName ?? code;

  // Per column, per row: parts + (for variants) word diffs against THAT column's own source —
  // the English it was made from, which is what the change should be judged against.
  const computed = columns.map((c) => {
    const variant = isEnglishVariant(c.locale, locales.data);
    const source = c.sourceFields ?? {};
    const cells = ROWS.map((row) => {
      const parts = row.parts(c.fields ?? {});
      const sourceParts = row.parts(source);
      const { diffs, changedWords } = diffParts(sourceParts, parts);
      const identical =
        parts.length > 0 &&
        parts.length === sourceParts.length &&
        parts.every((p, i) => p.text === sourceParts[i]?.text);
      return { parts, diffs: variant ? diffs : undefined, summary: { changedWords, identical } };
    });
    const withContent = cells.filter((cell) => cell.parts.length > 0);
    return {
      variant,
      cells,
      changedWords: cells.reduce((sum, cell) => sum + cell.summary.changedWords, 0),
      changedFields: withContent.filter((cell) => cell.summary.changedWords > 0).length,
      untranslatedFields: withContent.filter((cell) => cell.summary.identical).length,
      fields: withContent.length,
    };
  });

  return (
    <div
      className="overflow-x-auto rounded-2xl border bg-white shadow-sm"
      data-testid="translations-compare-view"
    >
      <table
        className="w-full min-w-[720px] table-fixed text-sm"
        data-testid="translations-compare-table"
      >
        <thead>
          <tr className="border-b align-top">
            <th className="text-muted-foreground w-32 px-4 py-3 text-left text-xs font-medium">
              Field
            </th>
            <th className="px-4 py-3 text-left">
              <span className="block font-semibold">English</span>
              <span className="text-muted-foreground text-xs">en-US · source</span>
            </th>
            {columns.map((c, index) => (
              <th
                key={`${c.locale}-${c.enrichmentPhase ?? ''}`}
                className="px-4 py-3 text-left"
                data-testid={`translations-compare-column-${c.locale}`}
              >
                <span className="block font-semibold">{nameOf(c.locale)}</span>
                <span className="text-muted-foreground text-xs">{c.locale}</span>
                <span className="mt-1 flex flex-wrap gap-1">
                  {c.qaStatus && (
                    <Badge
                      variant="outline"
                      className={cn('rounded-md text-[10px]', QA_STYLE[c.qaStatus])}
                      data-testid={`translations-compare-column-${c.locale}-qa-badge`}
                    >
                      {QA_LABEL[c.qaStatus]}
                    </Badge>
                  )}
                  {c.stale && (
                    <Badge
                      variant="outline"
                      className="rounded-md border-amber-200 bg-amber-50 text-[10px] text-amber-700"
                      data-testid={`translations-compare-column-${c.locale}-stale-badge`}
                    >
                      Stale
                    </Badge>
                  )}
                  {c.promptVersion && (
                    <span className="text-muted-foreground text-[10px]">
                      prompt {c.promptVersion}
                    </span>
                  )}
                </span>
                <ColumnSummary
                  column={computed[index]}
                  testId={`translations-compare-column-${c.locale}-summary`}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row, rowIndex) => (
            <tr
              key={row.label}
              className="border-b align-top last:border-0"
              data-testid={`translations-compare-row-${row.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <td className="text-muted-foreground px-4 py-3 text-xs">{row.label}</td>
              <td
                className="px-4 py-3 whitespace-pre-line"
                data-testid={`translations-compare-cell-${row.label.toLowerCase().replace(/\s+/g, '-')}-en-US`}
              >
                <FieldValue parts={row.parts(english)} list={row.list} sourceLabel="en-US" />
              </td>
              {columns.map((c, colIndex) => {
                const column = computed[colIndex];
                const cell = column?.cells[rowIndex];
                return (
                  <td
                    key={`${c.locale}-${c.enrichmentPhase ?? ''}`}
                    className="px-4 py-3 whitespace-pre-line"
                    data-testid={`translations-compare-cell-${row.label.toLowerCase().replace(/\s+/g, '-')}-${c.locale}`}
                  >
                    {cell && cell.parts.length > 0 && (
                      <CellChip
                        summary={cell.summary}
                        variant={column.variant}
                        testId={`translations-compare-cell-${row.label.toLowerCase().replace(/\s+/g, '-')}-${c.locale}`}
                      />
                    )}
                    <div>
                      <FieldValue
                        parts={cell?.parts ?? []}
                        list={row.list}
                        {...(cell?.diffs ? { diffs: cell.diffs } : {})}
                        sourceLabel="en-US"
                      />
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {columns.some((c) => c.qaStatus === 'FAIL' && (c.qaReasons?.length ?? 0) > 0) && (
        <div
          className="space-y-1 border-t px-4 py-3 text-xs"
          data-testid="translations-compare-held-back-banner"
        >
          {columns
            .filter((c) => c.qaStatus === 'FAIL')
            .map((c) => (
              <p
                key={c.locale}
                className="text-destructive"
                data-testid={`translations-compare-held-back-item-${c.locale}`}
              >
                {c.locale} held back: {(c.qaReasons ?? []).join('; ')}
              </p>
            ))}
        </div>
      )}
    </div>
  );
}

/** Header line under a locale: how much it changed (variants) or what came back untranslated. */
function ColumnSummary({
  column,
  testId,
}: {
  testId: string;
  column:
    | {
        variant: boolean;
        changedWords: number;
        changedFields: number;
        untranslatedFields: number;
        fields: number;
      }
    | undefined;
}) {
  if (!column) return null;
  if (column.variant) {
    return (
      <span
        className="text-muted-foreground mt-1 block text-[11px] font-normal"
        data-testid={testId}
      >
        {column.changedWords === 0
          ? 'Identical to English (en-US)'
          : `${String(column.changedWords)} ${column.changedWords === 1 ? 'word' : 'words'} changed in ${String(column.changedFields)} of ${String(column.fields)} fields`}
      </span>
    );
  }
  return column.untranslatedFields > 0 ? (
    <span className="mt-1 block text-[11px] font-normal text-amber-700" data-testid={testId}>
      {column.untranslatedFields} of {column.fields} fields same as English
    </span>
  ) : null;
}

/**
 * The comparison, but only for a product that HAS translations — for a surface where an
 * English-only product must look exactly as it did before (the sample-review modal, which has
 * no tab to put an empty state behind). Shares the query with the view, so it costs no extra
 * request.
 */
export function LanguageCompareSection({ workflowId, productId }: LanguageCompareViewProps) {
  const { data } = useProductTranslations(workflowId, productId, workflowId !== '');
  if (!data || data.length === 0) return null;
  return (
    <section className="space-y-3" data-testid="translations-compare-section">
      <div>
        <h2
          className="text-foreground text-base font-semibold"
          data-testid="translations-compare-title"
        >
          Languages
        </h2>
        <p className="text-muted-foreground text-sm">
          English and each translation, field by field. Check them before approving delivery.
        </p>
      </div>
      <LanguageCompareView workflowId={workflowId} productId={productId} />
    </section>
  );
}

/** One entry per locale; FULL wins over SAMPLE because FULL is what ships. */
function pickColumns(rows: LocalizedContent[]): LocalizedContent[] {
  const byLocale = new Map<string, LocalizedContent>();
  for (const r of rows) {
    const current = byLocale.get(r.locale);
    if (!current || (current.enrichmentPhase !== 'FULL' && r.enrichmentPhase === 'FULL')) {
      byLocale.set(r.locale, r);
    }
  }
  return [...byLocale.values()];
}
